import { CommonModule, Location } from '@angular/common'
import { Component, OnInit, ViewEncapsulation } from '@angular/core'
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router'
import { filter } from 'rxjs/operators'
import { I18nService } from '../../i18n/service/i18n.service'
import { ProfileManagementService } from '../../service/store-management/profile-management/profile-management.service'
import { Breadcrumb } from './model/breadcrumb'
import { StorageService } from '../../service/storage/storage.service'
import { I18nPipe } from '../../i18n/pipe/i18n.pipe'

@Component({
	selector: 'rfc-breadcrumb',
	templateUrl: './breadcrumb.component.html',
	styleUrls: ['./breadcrumb.component.scss'],
	encapsulation: ViewEncapsulation.None,
	standalone: true,
	imports: [
		CommonModule
	]
})
export class BreadcrumbComponent implements OnInit {
	public breadcrumbs: Breadcrumb[]

	previousUrl: string
	currentUrl: string

	constructor(
		private router: Router,
		private route: ActivatedRoute,
		private i18nService: I18nService,
		private location: Location,
		private profileManagement: ProfileManagementService,
		private storageService: StorageService
	) {
		router.events.subscribe((event) => {
			if (event instanceof NavigationEnd) {
				this.previousUrl = this.currentUrl
				this.currentUrl = event.url
			}
		})
	}

	ngOnInit(): void {
		const isOffline = this.storageService.getSessionStorage('offline')
		const breadcrumb: Breadcrumb = {
			label: 'Home',
			url: !!!isOffline ? '/home' : '',
		}

		this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
			// set breadcrumbs
			const root: ActivatedRoute = this.route.root
			this.breadcrumbs = this.getBreadcrumbs(root)
			if (this.breadcrumbs && this.breadcrumbs.length > 0) {
				this.breadcrumbs.unshift(breadcrumb)
			}
		})
	}

	private getBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: Breadcrumb[] = []): Breadcrumb[] {
		const ROUTE_DATA_BREADCRUMB = 'title'
		const PARENT_ROUTE = 'parentRoute'
		const PARENT_TITLE = 'parentTitle'
		// get the child routes
		const children: ActivatedRoute[] = route.children

		// return if there are no more children
		if (children.length === 0) {
			return breadcrumbs
		}

		// iterate over each children
		for (const child of children) {
			// verify the custom data property "breadcrumb" is specified on the route
			if (!child.snapshot.data.hasOwnProperty(ROUTE_DATA_BREADCRUMB)) {
				return this.getBreadcrumbs(child, url, breadcrumbs)
			}

			// get the route's URL segment
			const routeURL: string = child.snapshot.url.map((segment) => segment.path).join('/')

			// append route URL to URL
			url += `/${routeURL}`

			if (!!child?.snapshot?.data[PARENT_ROUTE]) {
				breadcrumbs.push(this.getElement(child.snapshot.data[PARENT_TITLE], child.snapshot.data[PARENT_ROUTE]))
			}
			const { parentTitle, parentUrl } = this.getParent(child.snapshot.data)
			if (!!parentTitle) {
				breadcrumbs.push(this.getElement(parentTitle, parentUrl))
			}

			breadcrumbs.push(this.getElement(child.snapshot.data[ROUTE_DATA_BREADCRUMB], null))

			return this.getBreadcrumbs(child, url, breadcrumbs)
		}
		return breadcrumbs
	}

	navigateTo(breadcrumb: Breadcrumb): void {
		if (!!breadcrumb?.url) {
			this.router.navigate([breadcrumb.url])
		}
	}

	getElement(title: string, url: string): Breadcrumb {
		return {
			label: this.i18nService.getTranslation(title),
			url,
		}
	}

	getParent(data: Data): any {
		const { parents } = data
		if (parents?.length > 0) {
			const [mainParent] = parents
			const isEnabled = this.checkIfValueIsEnabled(mainParent)
			if (!isEnabled) {
				return {}
			}
			if (!!this.previousUrl) {
				const parentObj = parents.find((el) => `/${el.parentUrl}` === this.previousUrl)
				return !!parentObj ? parentObj : mainParent
			} else {
				return mainParent
			}
		}
		return {}
	}

	private checkIfValueIsEnabled(menuEl: any): boolean {
		if (!!menuEl?.cod) {
			const MENU = this.profileManagement.getMenu()
			const category = MENU.find((el) => el.cod === menuEl.cod)
			const flatted = category?.child.flat(Infinity)
			const element = flatted.find((el) => el.path === `/${menuEl.parentUrl}`)
			return !!element
		}
		return true
	}
}
